import type { ChartPoint } from '@/admin/dashboards/chartViewModel';

const SCORE_AXIS_FLOOR = 20;
const SCORE_AXIS_MIN_CEILING = 100;

function collectBandValues(points: ChartPoint[]): number[] {
  const values: number[] = [];
  for (const point of points) {
    if (typeof point.value === 'number') values.push(point.value);
    const { p20, p80 } = point;
    if (typeof p20 === 'number') values.push(p20);
    if (typeof p80 === 'number') values.push(p80);
  }
  return values;
}

function ceilingStrictMultipleOf25(highest: number): number {
  const next = (Math.floor(highest / 25) + 1) * 25;
  return Math.max(SCORE_AXIS_MIN_CEILING, next);
}

/**
 * Y domain for score progression (% of pass mark).
 * Floor 20 unless any band edge is below 20 (then 0).
 * Ceiling is the smallest multiple of 25 strictly above the highest edge, at least 100.
 */
export function scoreChartYDomain(points: ChartPoint[] | null | undefined): [number, number] {
  if (!points?.length) {
    return [SCORE_AXIS_FLOOR, SCORE_AXIS_MIN_CEILING];
  }
  const bandValues = collectBandValues(points);
  if (!bandValues.length) {
    return [SCORE_AXIS_FLOOR, SCORE_AXIS_MIN_CEILING];
  }
  const highest = Math.max(...bandValues);
  const floor = bandValues.some((v) => v < SCORE_AXIS_FLOOR) ? 0 : SCORE_AXIS_FLOOR;
  const ceiling = ceilingStrictMultipleOf25(highest);
  return [floor, ceiling];
}

/**
 * Explicit Y-axis tick values for score progression.
 * Step 20 when floor and ceiling are both multiples of 20; otherwise 25.
 */
export function scoreChartYTicks(domain: [number, number]): number[] {
  const [floor, ceiling] = domain;
  const step = floor % 20 === 0 && ceiling % 20 === 0 ? 20 : 25;
  const ticks: number[] = [];
  let tick = Math.ceil(floor / step) * step;
  if (floor % step === 0) {
    tick = floor;
  }
  while (tick <= ceiling) {
    ticks.push(tick);
    tick += step;
  }
  if (floor <= 100 && ceiling >= 100 && !ticks.includes(100)) {
    ticks.push(100);
    ticks.sort((a, b) => a - b);
  }
  return ticks;
}
